// =============================================================================
// SIREPPAR - Contexto de Autenticación
// =============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

// =============================================================================
// Tipos
// =============================================================================

interface User {
  id: string;
  email: string;
  nombre: string;
  apellido: string;
  dni?: string;
  rol: string;
  partidoId?: string;
  mfaHabilitado: boolean;
  estado?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresMFA: boolean;
}

interface LoginCredentials {
  email: string;
  password: string;
  mfaCode?: string;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ requiresMFA: boolean; user?: User }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// =============================================================================
// Contexto
// =============================================================================

const AuthContext = createContext<AuthContextType | null>(null);

// =============================================================================
// Provider
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    requiresMFA: false,
  });

  const navigate = useNavigate();

  // Verificar sesión al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setState(s => ({ ...s, isLoading: false }));
        return;
      }

      try {
        const response = await api.get('/auth/me');
        setState({
          user: response.data.data,
          isAuthenticated: true,
          isLoading: false,
          requiresMFA: false,
        });
      } catch {
        localStorage.removeItem('accessToken');
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          requiresMFA: false,
        });
      }
    };

    checkAuth();
  }, []);

  // Login
  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await api.post('/auth/login', credentials);
    const { data } = response.data;

    if (data.requiresMFA) {
      setState(s => ({ ...s, requiresMFA: true }));
      return { requiresMFA: true };
    }

    localStorage.setItem('accessToken', data.accessToken);
    
    setState({
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
      requiresMFA: false,
    });

    return { requiresMFA: false, user: data.user };
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Continuar con logout local aunque falle el servidor
    }

    localStorage.removeItem('accessToken');
    
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      requiresMFA: false,
    });

    navigate('/login');
  }, [navigate]);

  // Refrescar usuario
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setState(s => ({ ...s, user: response.data.data }));
    } catch {
      await logout();
    }
  }, [logout]);

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }

  return context;
}

// =============================================================================
// HOC para proteger rutas
// =============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }

    if (!isLoading && isAuthenticated && roles && user) {
      if (!roles.includes(user.rol)) {
        navigate('/unauthorized', { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate, roles, user]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (roles && user && !roles.includes(user.rol)) {
    return null;
  }

  return <>{children}</>;
}